import * as Sentry from '@sentry/tanstackstart-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { getMeOptions } from '#modules/user/model/me';
import { ApiError } from '#features/api/utils/ApiError';
import { USER_NOT_AUTH_CODE } from '#features/api/utils/gqlFetcher';
import { applyIdentity, forgetIdentity, rememberIdentity } from '#features/analytics/sentry/identity';
import { useAuth } from '../../auth/hooks/useAuth';
import { useIsAuth } from '../../auth/hooks/useIsAuth';

/** Общая на вкладку отметка «выход уже запрошен» — сбрасывается, когда сессия честно кончилась. */
let unauthorizedLogoutRequested = false;

export const useProfile = () => {
  const isAuth = useIsAuth();
  const queryClient = useQueryClient();
  const {
    data: userData,
    isLoading,
    status,
    error,
  } = useQuery({
    ...getMeOptions(),
    enabled: isAuth,
  });
  const { logout } = useAuth();

  const profile = useMemo(() => {
    if (userData?.__typename === 'CurrentUser') {
      return userData ?? null;
    }

    return null;
  }, [userData]);
  const profileSource = (profile as { source?: string } | null)?.source;
  const isTemporarilyUnavailable = useMemo(() => profileSource === 'UNAVAILABLE', [profileSource]);

  useEffect(() => {
    if (!isAuth || profile?.deleted) {
      forgetIdentity();
      Sentry.setUser(null);
      Sentry.setTag('country', 'unknown');
      return;
    }

    // Профиль ещё едет — личность НЕ сбрасываем. На старте она восстановлена из хранилища
    // (см. sentry/identity), и именно она подписывает сетевые ошибки, при которых `me` не
    // загрузится вовсе. Раньше здесь стоял setUser(null), и самый массовый класс ошибок
    // уезжал в GlitchTip без владельца (сбой DNS на Android 25.08: поле user пустое).
    if (!profile) {
      return;
    }

    const identity = {
      id: profile.id,
      username: profile.displayName ?? undefined,
      sessionId: profile.sessionId ?? undefined,
    };
    rememberIdentity(identity);
    applyIdentity(identity);

    if (profile.country?.code) {
      Sentry.setTag('country', profile.country.code);
    } else {
      Sentry.setTag('country', 'unknown');
    }
  }, [isAuth, profile]);

  // `isAuth` comes from the router context and lags behind the `me` request: right after
  // sign-out (or right after registration, before the cookie reaches the API) the context still
  // says "authed" while `me` already answers 401. Logging out invalidates the router, which
  // re-renders and re-evaluates that same condition — and every refetch brings a fresh error
  // object, so the effect fires again. Latch it: one logout per authenticated session.
  const logoutRequestedRef = useRef(false);

  useEffect(() => {
    if (!isAuth) {
      logoutRequestedRef.current = false;
      unauthorizedLogoutRequested = false;
    }
  }, [isAuth]);

  const requestLogout = useCallback(() => {
    // Защёлка нужна и на уровне модуля, а не только инстанса: useProfile живёт во многих
    // компонентах сразу, а свежесмонтированный инстанс приходит с чистым ref. Пока сбой
    // checkIsAuth означал «разлогинен», isAuth падал в false и всё само затихало; теперь при
    // недоступном checkIsAuth контекст остаётся авторизованным, и без общей защёлки каждый новый
    // инстанс дёргал бы logout заново.
    if (logoutRequestedRef.current || unauthorizedLogoutRequested) {
      return;
    }

    logoutRequestedRef.current = true;
    unauthorizedLogoutRequested = true;
    logout();
  }, [logout]);

  useEffect(() => {
    if (isAuth && profile?.deleted) {
      requestLogout();
    }
  }, [isAuth, requestLogout, profile?.deleted]);

  useEffect(() => {
    // Sign out only when our gateway itself said the token is no good. A bare 401/403 is
    // produced by something in front of us (bot challenge, WAF, proxy) and carries no
    // verdict about the token — acting on it used to wipe a perfectly good session, in
    // the browser and in the app, off a single unlucky response.
    const gatewayRejectedTheToken =
      error instanceof ApiError && error.errorCode === USER_NOT_AUTH_CODE;

    if (isAuth && gatewayRejectedTheToken) {
      requestLogout();
    }
  }, [error, isAuth, requestLogout]);

  const invalidateProfile = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['me'] }),
    [queryClient]
  );

  return {
    profile,
    isLoading,
    refetchProfile: invalidateProfile,
    status,
    isTemporarilyUnavailable,
  };
};
